// src/components/LeniaViewer.tsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAnimation } from '../hooks/useAnimation';
import { KernelConfig, ParamSelection, PolygonSelection } from '../types/lenia';
import { formatNumber } from '../utils/numberUtils';
import { loadInitialPattern } from "../utils/leniaDataAdapter";

interface LeniaViewerProps {
  kernel: KernelConfig;
  params: ParamSelection;
  polygonSelection: PolygonSelection | null;
  /** Parent-driven: true while polygon chart is deciding / fetching */
  selectionLoading?: boolean;
  pyodide: any;
  pyodideLoaded: boolean;
  pyodideError: string | null;
}

export const LeniaViewer: React.FC<LeniaViewerProps> = ({
  kernel,
  params,
  polygonSelection,
  selectionLoading = false,
  pyodide,
  pyodideLoaded,
  pyodideError
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const [isRunning, setIsRunning] = useState(true);
  const [fps, setFps] = useState(50);

  // Internal load (engine init/state copy). We keep a delayed visual state for flicker control.
  const [loading, setLoading] = useState(false);
  const [showLoading, setShowLoading] = useState(false);

  // One single continuous overlay gate during handoffs
  const [transitioning, setTransitioning] = useState(false);

  const [error, setError] = useState<string | null>(null);
  const [leniaInitialized, setLeniaInitialized] = useState(false);

  // Simulation resolution (fixed; CSS scales visually)
  const [gridSize, setGridSize] = useState<number>(100);
  const dt = 0.1;

  // Recording
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordedChunksRef = useRef<BlobPart[]>([]);
  const [isRecording, setIsRecording] = useState(false);
  const discardOnStopRef = useRef(false);

  // Buffers & lifecycle helpers
  const kernelSigRef = useRef<string | null>(null);
  const imageDataRef = useRef<ImageData | null>(null);
  const initialStateRef = useRef<number[][] | null>(null);
  const currentGridRef = useRef<number | null>(null);
  const initTokenRef = useRef(0);

  // Track the target selection/key to detect handoffs
  const fetchAbortRef = useRef<AbortController | null>(null);
  const prevTargetKeyRef = useRef<string | null>(null);
  const targetKey = JSON.stringify({
    k: { f: kernel.funcK, mk: kernel.muK, sk: kernel.sigmaK, b: kernel.betas, r: kernel.kSize, t: kernel.tMax, g: kernel.gridSize },
    p: { mu: params.mu, sigma: params.sigma },
    sel: polygonSelection ? { s: polygonSelection.polygon_size, i: polygonSelection.sample_index } : null
  });

  // Show internal loader only if work takes >120ms (avoid flicker)
  useEffect(() => {
    let t: any;
    if (loading) t = setTimeout(() => setShowLoading(true), 120);
    else setShowLoading(false);
    return () => clearTimeout(t);
  }, [loading]);

  // Start a transition as soon as parent starts selecting (immediate)
  useEffect(() => {
    if (selectionLoading) setTransitioning(true);
  }, [selectionLoading]);

  // Start a transition when the target actually changes
  useEffect(() => {
    if (prevTargetKeyRef.current !== targetKey) {
      prevTargetKeyRef.current = targetKey;
      if (polygonSelection) {
        // New concrete selection → keep overlay on until first frame of new run is drawn
        setTransitioning(true);
      }
    }
  }, [targetKey, polygonSelection]);

  // Allocate a single ImageData whenever resolution changes
  useEffect(() => {
    imageDataRef.current = new ImageData(gridSize, gridSize);
  }, [gridSize]);

  // Tiny Python-side exporter
  async function ensureExporter(py: any) {
    await py.runPythonAsync(`
import numpy as np
if '_export_rgba' not in globals():
    def _export_rgba(step=False):
        if step:
            lenia.step()
        img = (lenia.draw().transpose(1, 2, 0) * 255).astype(np.uint8)
        H, W, _ = img.shape
        out = np.empty((H, W, 4), dtype=np.uint8)
        out[..., :3] = img
        out[..., 3]  = 255
        return out.tobytes()
    `);
  }

  // Unified renderer: step or not
  const renderFrame = useCallback(async (step: boolean) => {
    if (!pyodide || !canvasRef.current || !imageDataRef.current) return false;
    try {
      const hasLenia = await pyodide.runPythonAsync("globals().get('lenia') is not None");
      if (!hasLenia) return false;

      const bytes = await pyodide.runPythonAsync(`_export_rgba(${step ? 'True' : 'False'})`);
      const rgba = new Uint8ClampedArray(bytes.toJs());

      const ctx = canvasRef.current.getContext('2d');
      if (!ctx) return false;

      imageDataRef.current.data.set(rgba);
      ctx.putImageData(imageDataRef.current, 0, 0);
      return true;
    } catch (err) {
      setError(`${step ? 'Draw' : 'Render'} error: ${err}`);
      return false;
    }
  }, [pyodide]);

  // Animation: pause whenever transitioning (saves CPU and prevents stale visuals)
  const { start, stop, toggle } = useAnimation(() => renderFrame(true), 1000 / fps);
  useEffect(() => {
    if (leniaInitialized && isRunning && !transitioning && !selectionLoading) {
      start();
      Promise.resolve().then(() => renderFrame(false));
    } else {
      stop();
    }
    return () => { stop(); };
  }, [leniaInitialized, isRunning, transitioning, selectionLoading, start, stop, renderFrame]);

  // Apply new FPS immediately while running and not transitioning
  useEffect(() => {
    if (!leniaInitialized || !isRunning || transitioning || selectionLoading) return;
    stop(); start();
  }, [fps, isRunning, leniaInitialized, transitioning, selectionLoading, start, stop]);

  // (Re)initialize or update Lenia whenever kernel/params/polygon changes
  useEffect(() => {
    if (!pyodideLoaded || !pyodide || !polygonSelection) {
      setLeniaInitialized(false);
      setLoading(false);
      return;
    }

    const myToken = ++initTokenRef.current;

    // Abort previous in-flight pattern fetch (if any)
    fetchAbortRef.current?.abort();
    const controller = new AbortController();
    fetchAbortRef.current = controller;

    // Optional: micro-debounce to coalesce bursts
    const debounce = setTimeout(() => {
      (async () => {
        setLoading(true);
        setError(null);
        setLeniaInitialized(false);
        stop();

        try {
          const { grid, state } = await loadInitialPattern(
            kernel, params, polygonSelection, pyodide, controller.signal
          );
          if (controller.signal.aborted || initTokenRef.current !== myToken) return;

          setGridSize(grid);
          initialStateRef.current = state;
          await pyodide.globals.set("init_state_js", state);

          const kernelSig = JSON.stringify({
            fk: kernel.funcK, mk: kernel.muK, sk: kernel.sigmaK,
            b: kernel.betas, ks: kernel.kSize,
          });
          const mustRebuild = currentGridRef.current !== grid || kernelSigRef.current !== kernelSig;

          if (mustRebuild) {
            await pyodide.runPythonAsync(`
  import numpy as np
  params = LeniaParams(
    mu=${params.mu}, sigma=${params.sigma},
    mu_k=${kernel.muK ?? 0.0}, sigma_k=${kernel.sigmaK ?? 0.0},
    beta=[${kernel.betas.join(",")}],
    k_size=${kernel.kSize * 2 + 1},
    func_k="${kernel.funcK}"
  )
  lenia = MultiLeniaNumPy((${grid}, ${grid}), lenia_params=params, dt=${dt})
  lenia.state = np.array(init_state_js, dtype=np.float64).reshape(1,1,${grid},${grid})
            `);
            currentGridRef.current = grid;
            kernelSigRef.current = kernelSig;
          } else {
            await pyodide.runPythonAsync(`
  import numpy as np
  lenia.lenia_params.mu[...]    = ${params.mu}
  lenia.lenia_params.sigma[...] = ${params.sigma}
  lenia.g_mu  = np.round(lenia.lenia_params.mu.item(),    4)
  lenia.g_sig = np.round(lenia.lenia_params.sigma.item(), 4)
  init_state = np.array(init_state_js, dtype=np.float64).reshape(1,1,${grid},${grid})
  lenia.state[...] = init_state
            `);
          }

          await ensureExporter(pyodide);
          if (controller.signal.aborted || initTokenRef.current !== myToken) return;

          setLeniaInitialized(true);
          await renderFrame(false);
          setTransitioning(false);
        } catch (err: any) {
          // Ignore aborts; they’re expected during rapid clicks
          if (err?.name === 'AbortError') return;
          if (initTokenRef.current !== myToken) return;
          setError(`Failed to load pattern: ${err}`);
        } finally {
          if (initTokenRef.current === myToken) setLoading(false);
        }
      })();
    }, 60); // ← small debounce; tune or remove if you prefer

    return () => {
      clearTimeout(debounce);
      controller.abort(); // cancel fetches if dependencies change again
    };
  }, [kernel, params, polygonSelection, pyodide, pyodideLoaded, stop, renderFrame]);

  const handleToggleRun = useCallback(() => {
    setIsRunning(prev => toggle(prev));
  }, [toggle]);

  const handleStep = useCallback(() => {
    if (!isRunning && !transitioning && !selectionLoading) renderFrame(true);
  }, [renderFrame, isRunning, transitioning, selectionLoading]);

  const handleReset = useCallback(async () => {
    if (!pyodide || !initialStateRef.current) return;
    try {
      const wasRunning = isRunning;
      if (wasRunning) setIsRunning(false);

      await pyodide.globals.set("init_state_js", initialStateRef.current);
      await pyodide.runPythonAsync(`
import numpy as np
lenia.state = np.array(init_state_js, dtype=np.float64).reshape(1,1,${gridSize},${gridSize})
      `);

      await renderFrame(false);
      if (wasRunning) setIsRunning(true);
    } catch (e) {
      setError(`Reset error: ${e}`);
    }
  }, [pyodide, renderFrame, isRunning, gridSize]);

  // MediaRecorder helpers
  const pickMimeType = () => {
    const types = ['video/webm;codecs=vp9','video/webm;codecs=vp8','video/webm'];
    if (typeof (window as any).MediaRecorder === 'undefined') return '';
    for (const t of types) {
      // @ts-ignore
      if (MediaRecorder.isTypeSupported?.(t)) return t;
    }
    return '';
  };

  const sanitize = (s: string) => s.replace(/[^a-zA-Z0-9._-]/g, '_');
  const buildVideoFilename = () => {
    const parts = [
      'lenia',
      `fk_${kernel.funcK}`,
      kernel.funcK === 'gauss' ? `muK_${formatNumber(kernel.muK!)}` : '',
      kernel.funcK === 'gauss' ? `sigK_${formatNumber(kernel.sigmaK!)}` : '',
      `k${kernel.kSize}`,
      `betas_${kernel.betas.map(b => formatNumber(b)).join('-')}`,
      `mu_${formatNumber(params.mu)}`,
      `sig_${formatNumber(params.sigma)}`,
      `grid_${kernel.gridSize}`,
      `tMax_${kernel.tMax}`,
      `poly_${polygonSelection?.polygon_size ?? 'na'}`,
      `sample_${polygonSelection?.sample_index ?? 'na'}`,
    ];
    parts.push(new Date().toISOString().replace(/[:.]/g, '-'));
    return sanitize(parts.join('__')) + '.webm';
  };

  const handleToggleRecording = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    if (!isRecording) {
      recordedChunksRef.current = [];
      const stream = canvas.captureStream(Math.min(60, Math.max(1, fps)));

      let mr: MediaRecorder;
      const mimeType = pickMimeType();

      try {
        mr = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      } catch {
        try { mr = new MediaRecorder(stream); }
        catch { setError('Recording not supported in this browser.'); return; }
      }

      mediaRecorderRef.current = mr;

      mr.ondataavailable = (e: any) => {
        if (e.data && e.data.size > 0) recordedChunksRef.current.push(e.data);
      };
      mr.onstop = () => {
        if (discardOnStopRef.current) {
          discardOnStopRef.current = false;
          recordedChunksRef.current = [];
          return;
        }
        const type = (mr.mimeType && mr.mimeType.length) ? mr.mimeType : 'video/webm';
        const blob = new Blob(recordedChunksRef.current, { type });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = buildVideoFilename();
        a.click();
        URL.revokeObjectURL(url);
      };

      mr.start();
      setIsRecording(true);
    } else {
      mediaRecorderRef.current?.stop();
      setIsRecording(false);
    }
  }, [fps, isRecording, kernel, params, polygonSelection]);

  // Space to toggle play/pause unless typing
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const ae = document.activeElement as HTMLElement | null;
      if (ae && (ae.tagName === 'INPUT' || ae.tagName === 'TEXTAREA' || ae.isContentEditable)) return;
      if (e.code === 'Space') {
        e.preventDefault();
        setIsRunning(prev => toggle(prev));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [toggle]);

  // ────────────────────────────────────────────────────────────────────────────
  // Overlay logic: ONE continuous overlay across selection → init → first frame
  const overlayActive = selectionLoading || transitioning || showLoading;
  const overlayText =
    !polygonSelection
      ? (overlayActive ? 'Loading…' : 'Choose a run…')
      : (overlayActive ? 'Loading…' : null);

  const controlsDisabled =
    overlayActive || !polygonSelection || !leniaInitialized;

  return (
    <div className="lv">
      <style>{`
        .lv {
          --lv-scale: 1.18;
          --lv-radius: 0.75rem;
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
          width: 100%;
          font-size: calc(1rem * var(--lv-scale));
          position: relative;
        }
        @media (min-width: 87.5rem) { .lv { --lv-scale: 1.25; } }
        @media (max-width: 45rem)   { .lv { --lv-scale: 1.10; } }

        .lv-title {
          text-align: center;
          font-size: 1rem;
          color: #94a3b8;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace;
        }

        .lv-canvasframe {
          position: relative;
          width: 100%;
          background: #0b1220;
          border: 0.0625rem solid #334155;
          border-radius: var(--lv-radius);
          padding: 0.5rem;
        }

        .lv-overlay {
          position: absolute; inset: 0;
          display: flex; align-items: center; justify-content: center;
          background: #0b1220; /* opaque to fully hide old frame */
          border-radius: var(--lv-radius);
          z-index: 2; color: #94a3b8; font-size: 0.95rem;
          pointer-events: none;
          opacity: 1;
          transition: opacity .12s ease;
        }

        .lv-canvas {
          width: 100%;
          height: auto;
          aspect-ratio: 1 / 1;
          image-rendering: pixelated;
          display: block;
          border-radius: calc(var(--lv-radius) - 0.25rem);
          background: #0b1220;
          z-index: 1;
        }

        .lv-row { display: flex; gap: 0.5rem; justify-content: center; align-items: center; flex-wrap: wrap; }
        .lv-btn {
          padding: 0.5rem 0.9rem;
          border-radius: 0.625rem;
          border: 0.0625rem solid #334155;
          color: #fff;
          font-size: 1.1rem;
          font-weight: 700;
          cursor: pointer;
          background: #475569;
        }
        /* phase-based actions */
        .lv-btn.primary { background: var(--primary); }
        .lv-btn.danger  { background: var(--danger); }
        .lv-btn.info    { background: var(--info); }
        .lv-btn.save {
          background: var(--stable);   /* beige */
          color: #0b0f14;
        }
        .lv-btn.recording {
          background: var(--transition); /* yellow-orange */
          color: #0b0f14;
        }
        .lv-btn[disabled] { cursor: not-allowed; opacity: 0.6; }

        .lv-fps-wrap {
          display: flex; justify-content: center; margin-top: 0.25rem;
          padding-bottom: 0.6rem; border-bottom: 0.0625rem solid #334155;
        }
        .lv-label {
          color: #cbd5e1; font-size: 1.1rem; display: flex; align-items: center; gap: 0.5rem;
        }
        .lv-input {
          width: 7.5ch;
          padding: 0.35rem 0.45rem;
          border: 0.0625rem solid #334155;
          border-radius: 0.5rem;
          background: #0f172a;
          color: #e2e8f0;
          font-size: 1.0rem;
          text-align: center;
        }
        .lv-error { color: #fda4af; font-size: 0.95rem; margin-top: 0.5rem; text-align: center; }
      `}</style>

      <div className="lv-title">μ={params.mu} · σ={params.sigma}</div>

      <div className="lv-canvasframe">
        {!!overlayText && <div className="lv-overlay">{overlayText}</div>}
        <canvas
          ref={canvasRef}
          width={gridSize}
          height={gridSize}
          className="lv-canvas"
        />
      </div>

      <div className="lv-row">
        <button
          data-recording-allowed="true"
          onClick={handleToggleRun}
          className={`lv-btn ${isRunning ? 'danger' : 'primary'}`}
          disabled={controlsDisabled}
        >
          {isRunning ? 'Pause' : 'Play'}
        </button>
        <button
          data-recording-allowed="true"
          onClick={handleStep}
          disabled={controlsDisabled || isRunning}
          className="lv-btn"
          style={{ background: isRunning ? '#1f2937' : '#475569', color: isRunning ? '#6b7280' : '#fff' }}
        >
          Step
        </button>
        <button
          data-recording-allowed="true"
          onClick={handleReset}
          title="Reset to initial state"
          className="lv-btn"
          disabled={controlsDisabled}
        >
          Reset
        </button>
        <button
          data-recording-allowed="true"
          onClick={handleToggleRecording}
          title="Record/stop recording"
          className={`lv-btn ${isRecording ? 'recording' : 'save'}`}
          style={{ fontWeight: 800 }}
          disabled={!leniaInitialized || !polygonSelection || overlayActive}
        >
          {isRecording ? 'Stop & Save' : 'Save video'}
        </button>
      </div>

      <div className="lv-fps-wrap">
        <label className="lv-label">
          FPS
          <input
            data-recording-allowed="true"
            type="number"
            min={1}
            max={60}
            value={fps}
            onChange={(e) => setFps(Math.min(60, Math.max(1, Number(e.target.value) || 1)))}
            className="lv-input"
            disabled={overlayActive || !polygonSelection}
          />
        </label>
      </div>

      {error && <div className="lv-error">{String(error)}</div>}
    </div>
  );
};
